import { usePublicFormApi } from "services/api/PublicFormApi";
import { useParams } from "react-router";
import { useState, useEffect } from "react";
import {
  AlreadySubmitted,
  ErrorForm,
  FormSubmitted,
  LoadingForm
} from "modules/forms/components/PublicForm/StateViews";
import { PublicForm } from "modules/forms/components/PublicForm/PublicForm";

export type FormSubmission = {
  responseId: string;
  submission: { [key: string]: string | undefined };
};

export const PublicFormRoute = () => {
  const { id } = useParams();
  const api = usePublicFormApi();
  const { data: form, isLoading, isError, error } = api.find({ responseId: id! }).query;
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [backgroundImageLoaded, setBackgroundImageLoaded] = useState(false);

  const doSubmit = api.submit();

  const onSubmit = (submission: FormSubmission) => {
    return doSubmit.mutateAsync(submission).then(() => setIsSubmitted(true));
  };

  // Preload background image
  useEffect(() => {
    if (form?.branding?.backgroundImageUrl) {
      setBackgroundImageLoaded(false); // Reset state when URL changes
      const img = new Image();
      img.onload = () => {
        // Wait for browser to paint the background image
        setTimeout(() => {
          setBackgroundImageLoaded(true);
        }, 200);
      };
      img.onerror = () => setBackgroundImageLoaded(true); // Continue even if image fails to load
      img.src = form.branding.backgroundImageUrl;
    } else {
      // No background image to load
      setBackgroundImageLoaded(true);
    }
  }, [form?.branding?.backgroundImageUrl]);

  if (isLoading) {
    return <LoadingForm />;
  }

  if (form && isSubmitted) {
    return <FormSubmitted branding={form.branding} />;
  }

  if (isError || !form) {
    const isAlreadySubmitted = (error as any)?.code === 409;

    if (isAlreadySubmitted) {
      return <AlreadySubmitted />;
    }

    return <ErrorForm />;
  }

  // Render form with loading overlay if background not loaded yet
  const showLoading = !backgroundImageLoaded;

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      {showLoading && (
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, zIndex: 10 }}>
          <LoadingForm />
        </div>
      )}
      <div
        style={{
          opacity: showLoading ? 0 : 1,
          pointerEvents: showLoading ? "none" : "auto",
          transition: showLoading ? "none" : "opacity 0.3s ease-in-out"
        }}
      >
        <PublicForm form={form} onSubmit={values => onSubmit(values)} responseId={id!} />
      </div>
    </div>
  );
};
